/**
 * DENET — arama overlay'i
 * Predictive Search API'sinden sunucu tarafında üretilmiş HTML çeker.
 * İstemcide şablon üretilmez; gelen parça olduğu gibi yerleştirilir.
 */

import {
  DenetElement, define, qsa, debounce, EVENTS, on, lockScroll, unlockScroll,
} from '@denet/core';

const RECENT_KEY = 'denet:recent-searches';
const RECENT_MAX = 5;
const MIN_QUERY = 2;

class DnSearch extends DenetElement {
  /** @type {AbortController | null} */
  #fetchController = null;
  #idleMarkup = '';
  #activeIndex = -1;

  mounted() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.ref('dialog'));
    this.input = /** @type {HTMLInputElement | null} */ (this.ref('input'));
    this.results = this.ref('results');
    this.viewAll = this.ref('viewAll');

    if (!this.dialog || !this.input || !this.results) return;

    this.#idleMarkup = this.results.innerHTML;

    on(EVENTS.searchOpen, () => this.open(), { signal: this.signal });

    this.ref('close')?.addEventListener('click', () => this.close(), { signal: this.signal });

    this.dialog.addEventListener('close', () => {
      unlockScroll();
      this.#reset();
    }, { signal: this.signal });

    // Panelin dışına tıklama kapatır; panelin kendisi tıklamayı yutar.
    this.dialog.addEventListener('click', (event) => {
      const panel = this.querySelector('.dn-search__panel');
      if (panel instanceof HTMLElement && !panel.contains(/** @type {Node} */ (event.target))) {
        this.close();
      }
    }, { signal: this.signal });

    this.input.addEventListener('input', debounce(() => this.#search(), 220), { signal: this.signal });
    this.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal: this.signal });

    this.ref('form')?.addEventListener('submit', () => this.#remember(this.input?.value ?? ''), {
      signal: this.signal,
    });
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    lockScroll();
    this.#renderRecent();
    this.input?.focus();
  }

  close() {
    this.dialog?.close();
  }

  /* ------------------------------------------------------------- arama */

  async #search() {
    const query = this.input?.value.trim() ?? '';

    if (query.length < MIN_QUERY) {
      this.#reset();
      return;
    }

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    this.dataset.loading = 'true';

    const routes = window.DenetTheme?.routes;
    const url = new URL(routes?.predictiveSearch ?? '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('section_id', 'dn-predictive-results');

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fragment = parsed.querySelector('.dn-search__grid, .dn-search__state');

      if (this.results) {
        this.results.innerHTML = fragment ? fragment.outerHTML : '';
      }

      this.#activeIndex = -1;
      this.#updateViewAll(query);
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') {
        // Ağ hatasında sessizce boş bırakmak yerine tam arama sayfasına yönlendirme sunulur.
        this.#updateViewAll(query);
      }
    } finally {
      this.dataset.loading = 'false';
    }
  }

  /** @param {string} query */
  #updateViewAll(query) {
    if (!(this.viewAll instanceof HTMLAnchorElement)) return;
    const base = window.DenetTheme?.routes?.search ?? '/search';
    this.viewAll.href = `${base}?q=${encodeURIComponent(query)}`;
    this.viewAll.hidden = query.length < MIN_QUERY;
  }

  #reset() {
    this.#fetchController?.abort();
    if (this.results) this.results.innerHTML = this.#idleMarkup;
    this.#renderRecent();
    this.#activeIndex = -1;
    if (this.viewAll) this.viewAll.hidden = true;
  }

  /* ------------------------------------------------------- son aramalar */

  /** @returns {string[]} */
  #getRecent() {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  /** @param {string} query */
  #remember(query) {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) return;
    const list = [trimmed, ...this.#getRecent().filter((item) => item !== trimmed)].slice(0, RECENT_MAX);
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch {
      /* depolama kapalıysa sessizce geç */
    }
  }

  #renderRecent() {
    const wrapper = this.ref('recent');
    const list = this.ref('recentList');
    if (!wrapper || !list) return;

    const items = this.#getRecent();
    if (items.length === 0) {
      wrapper.hidden = true;
      return;
    }

    const base = window.DenetTheme?.routes?.search ?? '/search';
    list.innerHTML = items
      .map((item) => {
        const safe = item.replace(/[<>&"]/g, (c) =>
          ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c] ?? c
        );
        return `<a class="dn-search__link" href="${base}?q=${encodeURIComponent(item)}" data-result>${safe}</a>`;
      })
      .join('');
    wrapper.hidden = false;
  }

  /* --------------------------------------------------------- klavye */

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

    const items = /** @type {HTMLElement[]} */ (qsa('[data-result]', this));
    if (items.length === 0) return;

    if (event.key === 'Enter') {
      if (this.#activeIndex >= 0) {
        event.preventDefault();
        this.#remember(this.input?.value ?? '');
        items[this.#activeIndex]?.click();
      }
      return;
    }

    event.preventDefault();
    items[this.#activeIndex]?.classList.remove('is-active');

    const delta = event.key === 'ArrowDown' ? 1 : -1;
    this.#activeIndex = (this.#activeIndex + delta + items.length) % items.length;

    const active = items[this.#activeIndex];
    active?.classList.add('is-active');
    active?.scrollIntoView({ block: 'nearest' });
  }
}

define('dn-search', DnSearch);
